import { normalise } from "./discountCode";

/**
 * Affiliate referral codes (KEY-514): which affiliate sent this buyer. Carried as `?ref=PEP-JSMITH-7K` on a
 * link from the website (or an affiliate's own link straight here), remembered for 30 days, and sent with
 * the checkout. Nothing here decides whether a code is live — the server resolves it, ignores a dead one,
 * and never refuses a purchase over it.
 *
 * Last click wins: a newer `?ref` overwrites a remembered one. The server then keeps the FIRST referral it
 * records for a customer, permanently — the window only decides which link gets the credit at checkout.
 */

const STORAGE_KEY = "kd-ref";

/** How long a remembered referral counts for. */
export const REFERRAL_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

interface Remembered {
  code: string;
  at: number;
}

/** The referral code a link carried, tidied, or null when there is none or it is not the shape of one. */
export function readRef(search: string): string | null {
  return normalise(new URLSearchParams(search).get("ref"));
}

/**
 * The referral for this visit: the link's own `?ref` (remembered, replacing any older one), else one
 * remembered within the last 30 days, else null. Storage can throw (private windows, blocked site data);
 * then only a link's own code counts.
 */
export function currentRef(search: string, storage: Storage | null = safeLocalStorage(), now: number = Date.now()): string | null {
  const fromLink = readRef(search);
  try {
    if (fromLink) {
      storage?.setItem(STORAGE_KEY, JSON.stringify({ code: fromLink, at: now } satisfies Remembered));
      return fromLink;
    }
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as Partial<Remembered>;
    const code = normalise(saved.code);
    if (!code || typeof saved.at !== "number" || now - saved.at > REFERRAL_WINDOW_MS || saved.at > now) {
      storage?.removeItem(STORAGE_KEY);
      return null;
    }
    return code;
  } catch {
    return fromLink;
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The website, as deployed builds set it; empty in local dev, where links fall back to this app's buy page. */
const configuredSite = (((import.meta.env as Record<string, string | undefined>).VITE_SITE_URL ?? "").trim()).replace(
  /\/+$/,
  "",
);

/**
 * An affiliate's own link to hand out (KEY-514): their partner's page on the website when it is published,
 * else the home page, with `?ref=` on it — the website remembers it and carries it to checkout. With no
 * website configured (local dev) it is this app's buy page, so the link still works end to end.
 */
export function referralLinkUrl(
  code: string,
  partnerPageSlug: string | null,
  siteBase: string = configuredSite,
  appOrigin: string = typeof window === "undefined" ? "" : window.location.origin,
): string {
  const query = `?ref=${encodeURIComponent(code)}`;
  if (!siteBase) return `${appOrigin}/billing/plans${query}`;
  return partnerPageSlug ? `${siteBase}/partners/${partnerPageSlug}/${query}` : `${siteBase}/${query}`;
}
